const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PST_TIME_ZONE = 'America/Los_Angeles';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function getYmdInTimeZone(date, timeZone) {
  const d = date instanceof Date ? date : new Date(date);
  if (!d || Number.isNaN(d.getTime())) return null;

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(d).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return { year, month, day };
}

function utcMidnightFromYmd(ymd) {
  // Map "PST calendar day" -> UTC midnight instant so day-diff math won't be affected by DST.
  return new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day));
}

/**
 * Plan week rule for instructor availability:
 * - Mar 02 2026 - Mar 12 2026: can plan for April 2026
 * - Every 4 weeks (28 days) the window shifts; next window plans next month
 * - Uses local date for "today"
 * @returns {{ inPlanWeek: boolean, allowedYear?: number, allowedMonth?: number, windowStart?: Date, windowEnd?: Date }}
 *   If in plan week: { inPlanWeek: true, allowedYear, allowedMonth, windowStart, windowEnd }
 *   If not: { inPlanWeek: false }
 */
export function getPlanWeekForToday() {
  // Use PST/PDT "calendar day" for both "today" and reference dates.
  // Avoids DST causing 28 days to become e.g. 27.958...
  const todayYmd = getYmdInTimeZone(new Date(), PST_TIME_ZONE);
  if (!todayYmd) return { inPlanWeek: false };

  const today = utcMidnightFromYmd(todayYmd);
  const refStart = utcMidnightFromYmd({ year: 2026, month: 3, day: 2 }); // Mar 2, 2026
  const refEnd = utcMidnightFromYmd({ year: 2026, month: 3, day: 12 }); // Mar 12, 2026
  const periodDays = 28;

  const daysSinceRefStart = (today.getTime() - refStart.getTime()) / MS_PER_DAY;

  if (daysSinceRefStart < 0) {
    return { inPlanWeek: false };
  }

  const k = Math.floor(daysSinceRefStart / periodDays);
  const windowStart = new Date(refStart.getTime() + k * periodDays * MS_PER_DAY);
  const windowEnd = new Date(refEnd.getTime() + k * periodDays * MS_PER_DAY);

  if (today < windowStart || today > windowEnd) {
    return { inPlanWeek: false };
  }

  // Allowed month: April 2026 + k (April = 4)
  let allowedMonth = 4 + k;
  let allowedYear = 2026;
  while (allowedMonth > 12) {
    allowedMonth -= 12;
    allowedYear += 1;
  }
  return { inPlanWeek: true, allowedYear, allowedMonth, windowStart, windowEnd };
}

/**
 * Suggested initial month for manager calendar (same plan-month logic; no restriction).
 * When in plan week returns that plan month; otherwise returns current month.
 * @returns {{ year: number, month: number }} 1-based month
 */
export function getPlanMonthForManagerView() {
  const plan = getPlanWeekForToday();
  if (plan.inPlanWeek && plan.allowedYear != null && plan.allowedMonth != null) {
    return { year: plan.allowedYear, month: plan.allowedMonth };
  }
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/** Format date as "Mon DD YYYY" (e.g. "Mar 02 2026") */
export function formatPlanWindowDate(date) {
  if (!date || !(date instanceof Date)) return '';
  const ymd = getYmdInTimeZone(date, PST_TIME_ZONE);
  if (!ymd) return '';
  const mon = MONTH_SHORT[ymd.month - 1];
  const day = String(ymd.day).padStart(2, '0');
  const year = ymd.year;
  return `${mon} ${day} ${year}`;
}

/** Format plan month as "Month YYYY" (e.g. "April 2026") */
export function formatPlanMonthName(allowedYear, allowedMonth) {
  if (allowedMonth < 1 || allowedMonth > 12) return '';
  return `${MONTH_NAMES[allowedMonth - 1]} ${allowedYear}`;
}

/** First day of allowed month as YYYY-MM-DD */
export function getAllowedMonthStart(allowedYear, allowedMonth) {
  const y = String(allowedYear);
  const m = String(allowedMonth).padStart(2, '0');
  return `${y}-${m}-01`;
}

/** Last day of allowed month as YYYY-MM-DD */
export function getAllowedMonthEnd(allowedYear, allowedMonth) {
  const lastDay = new Date(allowedYear, allowedMonth, 0).getDate();
  const y = String(allowedYear);
  const m = String(allowedMonth).padStart(2, '0');
  return `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
}
